import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Generator from './pages/Generator';
import StudioPage from './pages/StudioPage';
import AgentPage from './pages/AgentPage';
import YouTubePage from './pages/YouTubePage';
import TikTokPage from './pages/TikTokPage';
import ViralEngine from './pages/ViralEngine';
import VariantesPage from './pages/VariantesPage';
import CoachPage from './pages/CoachPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ICPPage from './pages/ICPPage';
import AuthPage from './pages/AuthPage';
import { PrivacyPage, TermsPage } from './pages/LegalPages';
import { HooksPage, ScriptsPage, CalendarPage, PlansPage } from './pages/OtherPages';

const DEFAULT_PAGE = 'dashboard';

/** Toutes les routes valides de l'application. */
const VALID_PAGES = [
  'dashboard', 'studio', 'generator', 'hooks', 'scripts', 'calendar',
  'viral', 'agent', 'variantes', 'coach', 'analytics',
  'youtube', 'tiktok', 'icp', 'plans', 'privacy', 'terms',
] as const;

type PageId = typeof VALID_PAGES[number];

/** Détermine la page initiale à partir de l'URL (hash ou path). */
function resolveInitialPage(): PageId {
  try {
    const hash = window.location.hash.replace('#', '').split('?')[0].split('&')[0];
    const path = window.location.pathname.replace(/^\//, '');

    // OAuth callbacks — rediriger vers la page correspondante
    if (hash.includes('youtube-callback') || path.includes('youtube-callback')) return 'youtube';
    if (hash.includes('tiktok-callback') || path.includes('tiktok-callback')) return 'tiktok';
    // Le token OAuth arrive dans le hash → on est sur la page YouTube
    if (window.location.hash.includes('access_token')) return 'youtube';

    if ((VALID_PAGES as readonly string[]).includes(hash)) return hash as PageId;
    if ((VALID_PAGES as readonly string[]).includes(path)) return path as PageId;
  } catch (e) {
    console.warn('[App] resolveInitialPage:', e);
  }
  return DEFAULT_PAGE;
}

const LoadingScreen: React.FC = () => (
  <div style={{ minHeight: '100vh', background: '#0A0A0F', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 32, fontWeight: 800, color: '#F8F7FF', marginBottom: 12 }}>
        Contenu<span style={{ color: '#6C47FF' }}>IA</span>
      </div>
      <div style={{ width: 36, height: 36, border: '3px solid rgba(108,71,255,0.2)', borderTopColor: '#6C47FF', borderRadius: '50%', animation: 'appspin 0.9s linear infinite', margin: '0 auto 12px' }} />
      <div style={{ color: '#9898B8', fontSize: 13 }}>Chargement…</div>
      <style>{'@keyframes appspin { to { transform: rotate(360deg); } }'}</style>
    </div>
  </div>
);

const AppContent: React.FC = () => {
  const { user, loading, authError } = useAuth();
  const [page, setPage] = useState<PageId>(resolveInitialPage);

  // Navigation interne (déclenchée par les pages via CustomEvent)
  const navigate = useCallback((target: string) => {
    const next = (VALID_PAGES as readonly string[]).includes(target) ? (target as PageId) : DEFAULT_PAGE;
    setPage(next);
    // Remonter en haut à chaque changement de page
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (typeof detail === 'string') navigate(detail);
    };
    window.addEventListener('navigate', handler);
    return () => window.removeEventListener('navigate', handler);
  }, [navigate]);

  if (loading) return <LoadingScreen />;

  if (!user) return <AuthPage />;

  const PAGES: Record<PageId, React.ReactNode> = {
    dashboard: <Dashboard onNavigate={navigate} />,
    studio: <StudioPage />,
    generator: <Generator />,
    hooks: <HooksPage />,
    scripts: <ScriptsPage />,
    calendar: <CalendarPage />,
    viral: <ViralEngine />,
    agent: <AgentPage />,
    variantes: <VariantesPage />,
    coach: <CoachPage />,
    analytics: <AnalyticsPage />,
    youtube: <YouTubePage />,
    tiktok: <TikTokPage />,
    icp: <ICPPage />,
    plans: <PlansPage />,
    privacy: <PrivacyPage onBack={() => navigate('dashboard')} />,
    terms: <TermsPage onBack={() => navigate('dashboard')} />,
  };

  // Fallback explicite vers le Dashboard si la clé n'existe pas
  const currentPage = PAGES[page] ?? PAGES[DEFAULT_PAGE];

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0F', color: '#F8F7FF', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <style>{`
        html, body { overflow-x: hidden; max-width: 100%; }
        * { box-sizing: border-box; }
        img, video, canvas { max-width: 100%; }
        pre, code { white-space: pre-wrap; word-break: break-word; }

        @media (max-width: 768px) {
          .desktop-sidebar { display: none !important; }
          .main-content {
            margin-left: 0 !important;
            padding-top: 56px !important;
            padding-bottom: 84px !important;
            width: 100% !important;
            overflow-x: hidden;
          }
          .main-content > div { padding-left: 16px !important; padding-right: 16px !important; }
        }
        @media (min-width: 769px) {
          .main-content { margin-left: 220px !important; }
        }
      `}</style>

      <Sidebar activePage={page} onNavigate={navigate} />

      <main className="main-content" style={{ minHeight: '100vh', overflowX: 'hidden' }}>
        {authError && (
          <div style={{ margin: '16px 20px 0', padding: '12px 16px', background: 'rgba(255,68,68,0.08)', border: '1px solid rgba(255,68,68,0.25)', borderRadius: 12, color: '#FF8888', fontSize: 13 }}>
            ⚠️ {authError}
          </div>
        )}

        {currentPage}

        <footer style={{ padding: '20px 24px', borderTop: '1px solid #1A1A2E', marginTop: 40, display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <span onClick={() => navigate('privacy')} style={{ color: '#6C6C8A', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>Politique de confidentialité</span>
          <span onClick={() => navigate('terms')} style={{ color: '#6C6C8A', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>Conditions d'utilisation</span>
          <span style={{ color: '#6C6C8A', fontSize: 11 }}>© 2026 Chanbé IA</span>
        </footer>
      </main>
    </div>
  );
};

const App: React.FC = () => (
  <AuthProvider>
    <AppContent />
  </AuthProvider>
);

export default App;
